import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStudioUser } from "../hooks/useStudioUser";
import { useAuth } from "../contexts/AuthContext";
import { useStudioPlan } from "../hooks/useStudioPlan";
import UpgradePrompt from "../components/UpgradePrompt";
import { isKioskMode, createKioskSession } from "../utils/kioskAuth";

export default function KioskLogin({
  onEnableKiosk,
}: {
  onEnableKiosk?: (studioId: string) => void;
}) {
  const navigate = useNavigate();
  const { studioUser } = useStudioUser();
  const { getAuthToken } = useAuth();
  const { hasCapability, loading: planLoading } = useStudioPlan();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // Watch for kiosk session and redirect
  useEffect(() => {
    if (isKioskMode()) {
      navigate("/student-sign-in", { replace: true });
    }
  }, [navigate]);

  const handleEnableKiosk = async () => {
    if (!studioUser?.studio_id) return;

    setLoading(true);
    setError("");

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const result = await createKioskSession(studioUser.studio_id, authToken);

      if (!result.success) {
        throw new Error(result.error || "Failed to create kiosk session");
      }

      if (onEnableKiosk) {
        onEnableKiosk(studioUser.studio_id);
      }

      navigate("/student-sign-in", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center">Kiosk Login</h2>

        {!planLoading && !hasCapability('kiosk') ? (
          <UpgradePrompt capability="kiosk" />
        ) : (
          <>
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <button
          onClick={handleEnableKiosk}
          className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!studioUser?.studio_id || loading}
        >
          {loading ? "Enabling..." : "Enable Kiosk Mode"}
        </button>

        <div className="text-xs text-gray-500 text-center mt-4">
          Need to set or change your kiosk password? Go to{" "}
          <b>Settings &rarr; Security Settings</b> in the admin dashboard.
        </div>

        {/* Security Notice */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
          <p className="font-semibold">Security Notice:</p>
          <p>
            Kiosk mode creates a secure session that expires automatically after
            12 hours.
          </p>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
